// Anonymous presence beacon - lets the server (and the /status page) show how many
// students have Re:Vault open right now. Sends a random, anonymous id (never PII,
// never logged, not associated with anything) and one yes/no (a focus timer is running)
// on a light interval, and only while the tab is visible so it costs nothing on a
// backgrounded or low-end device.
//
// Every 60 s (+/-15% jitter, so a classroom that opened Re:Vault at the bell does not
// beacon in lockstep). The server counts a tab as online for 150 s after its last ping.
// The reply also carries the deployed build id, which the auto-updater (update.js)
// listens for as "rv:build", so open tabs learn about a deploy without polling
// /__rv/version separately. (Was every 25 s plus its own version polling: the two were
// the top two request paths on the origin, about 940k requests a day on 2026-10-01.)
import { takeCommons, returnCommons } from "../shared/commons.js";

const PID_KEY = "revault:pid";
export const PRESENCE_EVERY_MS = 60000;
const JITTER = 0.15;

function pid() {
	try {
		let v = localStorage.getItem(PID_KEY);
		if (!v) { v = Date.now().toString(36) + Math.random().toString(36).slice(2, 10); localStorage.setItem(PID_KEY, v); }
		return v;
	} catch (e) { return "a" + Math.random().toString(36).slice(2, 10); }
}

// The reply already carries the anonymous online count; keep the last one for the start page's
// "This week" card (docs/HOME-2026-10.md, F6). No extra request, nothing new is sent.
let online = 0;
const onlineSubs = new Set();
export function lastOnline() { return online; }
export function onOnline(fn) { onlineSubs.add(fn); return () => onlineSubs.delete(fn); }
function gotOnline(j) {
	const n = j && j.online;
	if (!Number.isInteger(n) || n < 0 || n === online) return;
	online = n;
	onlineSubs.forEach((fn) => { try { fn(n); } catch { /* a subscriber problem, not ours */ } });
}

// Study Hall's rounded "N focusing" figure rides on the same reply (once Study Hall is past its staff
// alpha). Subscribers of onOnline hear about it too.
let hall = null;
export function lastHall() { return hall; }
function gotHall(j) {
	const h = j && j.hall;
	const next = h && Number.isInteger(h.f) && h.f >= 5 ? { f: h.f, room: typeof h.room === "string" ? h.room : "", ph: h.ph === "focus" || h.ph === "break" ? h.ph : "", end: Number(h.end) || 0, m: h.m === "beta" ? "beta" : "open" } : null;
	if (JSON.stringify(next) === JSON.stringify(hall)) return;
	hall = next;
	onlineSubs.forEach((fn) => { try { fn(online); } catch { /* a subscriber problem, not ours */ } });
}

// "Studying now": the beacon carries one yes/no, whether a focus timer is running in this browser
// (the timer tool, a Study Hall room, or the Island's own timer). Nothing else about the session is
// sent. If the timer tab goes away without saying so, the yes runs out on its own.
const FOCUS_GRACE_MS = 2 * 60000;
const FOCUS_MAX_MS = 3 * 3600000;
// Each timer reports under its own name ("island", or the tab it runs in), so stopping one does not
// clear another. alive: optional check that the tab running it is still open.
const timers = new Map(); // source -> { until, alive }
// Every Re:Vault window in this browser shares one presence id, so a second window without a timer
// would send "no" over the first one's "yes". Each window notes when its own yes runs out under a
// local key (never sent), and a beacon says yes while any window's note is still running.
const FOCUS_KEY = "revault:focusing";
const WIN = Math.random().toString(36).slice(2, 10);
function shared() {
	try { const v = JSON.parse(localStorage.getItem(FOCUS_KEY)); return v && typeof v === "object" ? v : {}; } catch { return {}; }
}
function shareFocus(now, gone) {
	let until = 0;
	if (!gone) for (const f of timers.values()) until = Math.max(until, f.until);
	const all = shared();
	for (const k in all) if (k === WIN || !(all[k] > now)) delete all[k];
	if (until > now) all[WIN] = until;
	try { localStorage.setItem(FOCUS_KEY, JSON.stringify(all)); } catch { /* storage off: this window still counts its own timers */ }
}
export function setFocusing(source, phase, end, alive = null, now = Date.now()) {
	const key = String(source);
	if (phase !== "work" && phase !== "break") timers.delete(key);
	else {
		const e = Number(end) || 0;
		timers.set(key, { until: e > now ? e + FOCUS_GRACE_MS : now + FOCUS_MAX_MS, alive: typeof alive === "function" ? alive : null });
	}
	shareFocus(now);
}
export function focusing(now = Date.now()) {
	let dropped = false;
	for (const [k, f] of timers) {
		let ok = now < f.until;
		if (ok && f.alive) { try { ok = !!f.alive(); } catch { ok = false; } }
		if (ok) return true;
		timers.delete(k);
		dropped = true;
	}
	if (dropped) shareFocus(now);
	const all = shared();
	for (const k in all) if (k !== WIN && all[k] > now && all[k] - now <= FOCUS_MAX_MS + FOCUS_GRACE_MS) return true;
	return false;
}
// A closing window takes its yes with it (and puts it back if it comes out of the back/forward cache).
try {
	addEventListener("pagehide", () => shareFocus(Date.now(), true));
	addEventListener("pageshow", (e) => { if (e.persisted) shareFocus(Date.now()); });
} catch { /* not a browser */ }

// The rounded "N studying right now" figure from the reply (null when the server leaves it out,
// which it does below 10).
let studying = null;
export function lastStudying() { return studying; }
function gotStudying(j) {
	const n = j && j.st;
	const next = Number.isInteger(n) && n >= 10 ? n : null;
	if (next === studying) return;
	studying = next;
	onlineSubs.forEach((fn) => { try { fn(online); } catch { /* a subscriber problem, not ours */ } });
}

// Whether carry codes are on (the server's switch). Noted under a device key so the tools pages,
// which have no beacon of their own, know too. Off until a reply says on.
export const CARRY_KEY = "rv:carry";
let carry = (() => { try { return localStorage.getItem(CARRY_KEY) === "1"; } catch { return false; } })();
export function carryOn() { return carry; }
function gotCarry(j) {
	const next = !!j && j.c === 1;
	if (next === carry) return;
	carry = next;
	try { if (next) localStorage.setItem(CARRY_KEY, "1"); else localStorage.removeItem(CARRY_KEY); } catch { /* storage off: this tab still knows */ }
	onlineSubs.forEach((fn) => { try { fn(online); } catch { /* a subscriber problem, not ours */ } });
}

// The Commons garden from the reply: { s: stage, h: rounded hours (left out below 10), last }.
let garden = null;
export function lastGarden() { return garden; }
function gotGarden(j) {
	const g = j && j.g;
	const next = g && Number.isInteger(g.s) && g.s >= 0 ? { s: g.s, ...(Number.isInteger(g.h) && g.h >= 10 ? { h: g.h } : {}) } : null;
	if (JSON.stringify(next) === JSON.stringify(garden)) return;
	garden = next;
	onlineSubs.forEach((fn) => { try { fn(online); } catch { /* a subscriber problem, not ours */ } });
}

const nextDelay = () => Math.round(PRESENCE_EVERY_MS * (1 - JITTER + Math.random() * 2 * JITTER));

export function initPresence() {
	const id = pid();
	let timer = null;
	const ping = () => {
		if (document.hidden) return;
		// Finished focus minutes for the Commons garden ride along (shared/commons.js).
		const gm = takeCommons();
		let got = false; // the server answered, so the minutes arrived
		const giveBack = () => { if (gm && !got) { got = true; returnCommons(gm); } };
		try {
			fetch("/status/ping?id=" + encodeURIComponent(id) + (focusing() ? "&f=1" : "") + (gm ? "&gm=" + gm : ""), { cache: "no-store", keepalive: true })
				.then((r) => { if (!r.ok) { giveBack(); return null; } got = true; return r.json(); })
				.then((j) => {
					if (!j) return;
					// The server says how many minutes counted; the rest wait for a later beacon.
					if (gm && Number.isInteger(j.gc) && j.gc >= 0 && j.gc < gm) returnCommons(gm - j.gc);
					gotGarden(j);
					gotOnline(j);
					gotHall(j);
					gotStudying(j);
					gotCarry(j);
					if (typeof j.b === "string" && j.b) window.dispatchEvent(new CustomEvent("rv:build", { detail: j.b }));
					// A heads-up before an update; app/update.js shows it.
					if (j.n) window.dispatchEvent(new CustomEvent("rv:notice", { detail: j.n }));
					// Server engine switches (rollback without a release); app/proxy.js applies them.
					if (j.e && typeof j.e === "object") window.dispatchEvent(new CustomEvent("rv:engines", { detail: j.e }));
					// The sandbox-origin switch (Phase 1); app/sbx.js notes it for the next load.
					if (typeof j.sbx === "string") window.dispatchEvent(new CustomEvent("rv:sbx", { detail: j.sbx }));
					// Who gets Study Helper (off | staff | accounts | on); app/helper-dock.js shows its button.
					if (typeof j.h === "string") window.dispatchEvent(new CustomEvent("rv:helper", { detail: j.h }));
				})
				.catch(giveBack);
		} catch (e) { giveBack(); }
	};
	const loop = () => { timer = setTimeout(() => { ping(); loop(); }, nextDelay()); };
	const start = () => { if (timer) return; ping(); loop(); };
	const stop = () => { if (timer) { clearTimeout(timer); timer = null; } };
	document.addEventListener("visibilitychange", () => { if (document.hidden) stop(); else start(); });
	start();
}
